import { supabase } from './supabase';
import { logger } from './logger';

export interface AdminJobOffer {
  id: string;
  title: string;
  employerName: string;
  employerId: string;
  sectorCode: string;
  positionName: string;
  city: string;
  contractType: string;
  salaryMin: number | null;
  salaryMax: number | null;
  status: string;
  applicationsCount: number;
  requiredSkills: string[];
  createdAt: string;
  seedTag: string | null;
}

export async function fetchJobOffers(): Promise<AdminJobOffer[]> {
  const { data, error } = await supabase
    .from('job_offers')
    .select(`
      id,
      position_name,
      sector_code,
      establishment_city,
      contract_type,
      salary_min,
      salary_max,
      status,
      applications_count,
      required_skills,
      created_at,
      employer_id,
      seed_tag,
      employer_profiles!job_offers_employer_id_fkey ( company_name )
    `)
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('fetch_job_offers', 'offerService', { error: error.message });
    throw error;
  }
  logger.technical('fetch_job_offers_ok', 'offerService', { count: (data || []).length });

  return (data || []).map((o: any) => ({
    id: o.id,
    title: o.position_name || '',
    employerName: o.employer_profiles?.company_name || '',
    employerId: o.employer_id,
    sectorCode: o.sector_code || '',
    positionName: o.position_name || '',
    city: o.establishment_city || '',
    contractType: o.contract_type || '',
    salaryMin: o.salary_min,
    salaryMax: o.salary_max,
    status: o.status || 'active',
    applicationsCount: o.applications_count || 0,
    requiredSkills: Array.isArray(o.required_skills) ? o.required_skills : [],
    createdAt: new Date(o.created_at).toLocaleDateString('fr-FR'),
    seedTag: o.seed_tag || null,
  }));
}

// ─── Modération admin ────────────────────────────────────────

/**
 * Ferme une offre côté admin (statut closed_by_admin) + trace dans admin_logs.
 * S'appuie sur la policy "Admin update job_offers" (is_admin()).
 */
export async function closeOfferAsAdmin(
  offerId: string,
  reason: string,
): Promise<void> {
  const { error } = await supabase
    .from('job_offers')
    .update({ status: 'closed_by_admin' })
    .eq('id', offerId);

  if (error) {
    logger.error('admin_close_offer', 'offerService', { offerId, error: error.message });
    throw error;
  }
  logger.functional('admin_close_offer', 'offerService', { offerId, reason });
}

/**
 * Réactive une offre fermée par admin (repasse en status active).
 */
export async function reopenOfferAsAdmin(offerId: string): Promise<void> {
  const { error } = await supabase
    .from('job_offers')
    .update({ status: 'active' })
    .eq('id', offerId);

  if (error) {
    logger.error('admin_reopen_offer', 'offerService', { offerId, error: error.message });
    throw error;
  }
  logger.functional('admin_reopen_offer', 'offerService', { offerId });
}

/**
 * Supprime définitivement une offre côté admin (cascade sur applications).
 * Nécessite une policy admin DELETE (à compléter dans la migration 016 si absente).
 */
export async function deleteOfferAsAdmin(
  offerId: string,
  reason: string,
): Promise<void> {
  const { error } = await supabase
    .from('job_offers')
    .delete()
    .eq('id', offerId);

  if (error) {
    logger.error('admin_delete_offer', 'offerService', { offerId, error: error.message });
    throw error;
  }
  logger.functional('admin_delete_offer', 'offerService', { offerId, reason });
}

/**
 * Récupère les détails complets d'une offre (incl. required_skills + employer).
 */
export async function fetchAdminOfferDetail(offerId: string) {
  const { data, error } = await supabase
    .from('job_offers')
    .select(`
      id, position_name, sector_code, position_code,
      establishment_name, establishment_address, establishment_zipcode, establishment_city,
      contract_type, salary_min, salary_max, salary_type,
      work_duration, work_rhythm, years_experience,
      required_skills, required_languages, required_diplomas, advantages,
      other_info, status, applications_count, published_at, created_at,
      employer_id,
      employer_profiles!job_offers_employer_id_fkey ( company_name, email, phone )
    `)
    .eq('id', offerId)
    .single();

  if (error) {
    logger.error('fetch_admin_offer_detail', 'offerService', { offerId, error: error.message });
    throw error;
  }
  return data;
}

/**
 * Résout une liste de codes de compétences vers leur libellé FR via la DB.
 * Renvoie une map code -> name_fr ; les codes non trouvés ne figurent pas dans la map.
 */
export async function resolveSkillNames(codes: string[]): Promise<Record<string, string>> {
  const real = codes.filter((c) => typeof c === 'string' && c && !c.startsWith('custom:'));
  if (real.length === 0) return {};
  const { data } = await supabase
    .from('skills')
    .select('code, name_fr')
    .in('code', real);
  const map: Record<string, string> = {};
  for (const row of (data as any[]) || []) {
    if (row.code && row.name_fr) map[row.code] = row.name_fr;
  }
  return map;
}
